import { useState, useRef, useEffect } from 'react';
import { Loader2, Coffee, Play, Clock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useRegisterSession } from '@/hooks/useRegisterSession';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';

const PIN_LENGTH = 6;

export function PausedSessionOverlay() {
  const { activeSession, isSessionPaused, resumeSession } = useRegisterSession();
  const { profile, user } = useAuth();
  const [pin, setPin] = useState<string[]>(Array(PIN_LENGTH).fill(''));
  const [error, setError] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Check if user has a PIN set
  const { data: hasPinSet } = useQuery({
    queryKey: ['user-has-pin', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const { data } = await supabase
        .from('profiles')
        .select('pin_hash')
        .eq('id', user.id)
        .single();
      return !!data?.pin_hash;
    },
    enabled: !!user?.id && isSessionPaused,
  });

  // Calculate break duration
  const [breakDuration, setBreakDuration] = useState('0:00');

  useEffect(() => {
    if (!activeSession?.paused_at) return;

    const updateDuration = () => {
      const pausedAt = new Date(activeSession.paused_at!);
      const now = new Date();
      const diffMs = now.getTime() - pausedAt.getTime();
      const minutes = Math.floor(diffMs / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);
      setBreakDuration(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateDuration();
    const interval = setInterval(updateDuration, 1000);
    return () => clearInterval(interval);
  }, [activeSession?.paused_at]);

  // Focus first input when overlay appears
  useEffect(() => {
    if (isSessionPaused) {
      setPin(Array(PIN_LENGTH).fill(''));
      setError(null);
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [isSessionPaused]);

  const handlePinChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setError(null);

    // Auto-focus next input
    if (value && index < PIN_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === PIN_LENGTH - 1) {
      const fullPin = newPin.join('');
      handleResume(fullPin);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleResume = async (fullPin: string) => {
    try {
      await resumeSession.mutateAsync({ pin: fullPin });
    } catch (err: any) {
      setError(err.message || 'Invalid PIN. Please try again.');
      setPin(Array(PIN_LENGTH).fill(''));
      inputRefs.current[0]?.focus();
    }
  };

  const handleSubmit = () => {
    const fullPin = pin.join('');
    if (fullPin.length === PIN_LENGTH) {
      handleResume(fullPin);
    }
  };

  if (!isSessionPaused) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-background/95 backdrop-blur-md">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
        <div className="absolute inset-0 pattern-dots" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center text-center max-w-md px-6">
        {/* Break icon with animation */}
        <div className="relative mb-8">
          <div className="w-24 h-24 rounded-full bg-blue-500/10 flex items-center justify-center">
            <Coffee className="h-12 w-12 text-blue-500" />
          </div>
          <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center animate-pulse">
            <Clock className="h-4 w-4 text-white" />
          </div>
        </div>

        {/* Status text */}
        <h1 className="text-3xl font-display font-bold mb-2">
          On Break
        </h1>
        <p className="text-lg text-muted-foreground mb-2">
          {profile?.first_name ? `Hey ${profile.first_name}, ` : ''}
          your register is paused
        </p>

        {/* Break duration */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
          <Clock className="h-4 w-4" />
          <span>Break duration: <span className="font-mono font-medium text-foreground">{breakDuration}</span></span>
        </div>

        {/* Register info */}
        {activeSession && (
          <div className="mb-8 px-4 py-3 rounded-xl bg-muted/50 border">
            <p className="text-sm text-muted-foreground">
              Register: <span className="font-medium text-foreground">{activeSession.register_name}</span>
            </p>
          </div>
        )}

        {/* No PIN Set - Manager Required */}
        {hasPinSet === false ? (
          <div className="w-full bg-card rounded-2xl border border-amber-500/30 p-6 shadow-lg">
            <div className="flex items-center justify-center gap-2 text-amber-600 mb-4">
              <AlertCircle className="h-5 w-5" />
              <h2 className="text-lg font-semibold">No PIN Set</h2>
            </div>
            <p className="text-sm text-muted-foreground">
              You don't have a PIN configured for your account. Please ask a manager or administrator to resume your register.
            </p>
          </div>
        ) : (
          /* PIN Entry section */
          <div className="w-full bg-card rounded-2xl border p-6 shadow-lg">
            <h2 className="text-lg font-semibold mb-2">Enter your PIN to resume</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Use your 6-digit PIN to unlock and continue your shift
            </p>

            {/* PIN Input - 6 digits */}
            <div className="flex justify-center gap-2 mb-4 relative z-20">
              {pin.map((digit, index) => (
                <Input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className={cn(
                    'w-11 h-12 text-center text-xl font-bold pointer-events-auto cursor-text p-0',
                    error && 'border-red-500 focus-visible:ring-red-500'
                  )}
                  disabled={resumeSession.isPending}
                />
              ))}
            </div>

            {/* Error message */}
            {error && (
              <p className="text-sm text-red-500 text-center mb-4">{error}</p>
            )}

            {/* Resume button */}
            <Button
              variant="blaze"
              size="lg"
              className="w-full gap-2"
              onClick={handleSubmit}
              disabled={pin.join('').length !== PIN_LENGTH || resumeSession.isPending}
            >
              {resumeSession.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Resuming...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4" />
                  Resume Shift
                </>
              )}
            </Button>
          </div>
        )}

        {/* Help text */}
        <p className="text-xs text-muted-foreground mt-6">
          {hasPinSet === false
            ? 'Contact your manager to resume your shift or set up a PIN.'
            : 'Forgot your PIN? Ask a manager for help.'
          }
        </p>
      </div>

      {/* BlazePOS Branding Footer */}
      <div className="absolute bottom-6 left-0 right-0 flex flex-col items-center gap-2">
        <div className="flex items-center gap-2 text-muted-foreground/60">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center overflow-hidden">
            <img src="/blazepos-logo.png" alt="BlazePOS" className="w-6 h-6 object-contain" />
          </div>
          <span className="text-sm font-display font-semibold">BlazePOS</span>
        </div>
        <a
          href="https://blazepos.app"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-muted-foreground/40 hover:text-muted-foreground/60 transition-colors"
        >
          blazepos.app
        </a>
      </div>
    </div>
  );
}
